import loadable from "@loadable/component";
import ExcelJS from "exceljs";

import styles from "@maxis/components/components/InputControls/Button.module.css";

// programPath -> loadable component. React decides whether to keep a mounted page by
// component identity, so the loader must hand back the same component for the same
// path. Without this, every App re-render (e.g. selecting a menu) produced a new
// component type and React tore down and rebuilt the page that was on screen —
// on the node page that meant hundreds of cards, ~4s of layout per menu click.
const componentCache = new Map();

export const componentLoader = (programPath) => {
  const cached = componentCache.get(programPath);
  if (cached) return cached;

  // const component = loadable(() => import( /* @vite-ignore */ `@/pages${programPath}`));
  const modules = import.meta.glob("pages/**/*.jsx");
  const envMode = import.meta.env.MODE == "useLocal" ? true : false;

  const normalizedPath = programPath.replace(/\/+/g, "/").replace(/^\//, "");
  const fullPath = `/src/pages/${normalizedPath}`; // 절대 경로로 변경

  const component = envMode
    ? loadable(() => import(/* @vite-ignore */ fullPath))
    : loadable(() => modules[`${fullPath}.jsx`]());

  componentCache.set(programPath, component);
  return component;
};

/**
 * 파라미터가 배열인지 아닌지 체크
 * 배열: true
 * 그외: false
 * @param {*} arr
 * @returns
 */
const chkArray = (arr) => {
  if (arr && arr.length > 0) {
    return true;
  } else {
    return false;
  }
};

const EXCLUDED_EXCEL_BINDINGS = ["ROW_CHECK", "ROW_STATUS"];

const normalizeExcelValue = (value) => {
  if (value == null) return "";
  if (value instanceof Date) return value;
  if (typeof value !== "object") {
    return typeof value === "string" && value.substring(0, 1) === "'" ? value.substring(1) : value;
  }
  if (value.richText) return value.richText.map((item) => item.text).join("");
  if (value.text) return value.text;
  if (value.result != null) return value.result;
  if (value.formula) return value.result ?? "";
  return String(value);
};

const getCellMergedValue = (cell) => {
  if (cell?.isMerged && cell.master) return normalizeExcelValue(cell.master.value);
  return normalizeExcelValue(cell?.value);
};

export const getGridLikeColumns = (grid, includeColumns) => {
  const rawColumns = Array.from(grid?.columns || grid?._cols || []);
  return rawColumns
    .filter((column) => {
      const binding = column?.binding;
      const visible = column?.visible !== false;
      const hasBinding = binding != null && binding !== "";
      const notMeta = !EXCLUDED_EXCEL_BINDINGS.includes(binding);
      return hasBinding && visible && notMeta && (includeColumns ? includeColumns(column) : true);
    })
    .map((column) => ({
      ...column,
      header: column.header ?? column._hdr ?? column.name ?? column.binding,
      binding: column.binding,
    }));
};

export const getGridLikeRows = (grid) => {
  if (Array.isArray(grid?.itemsSource)) return grid.itemsSource;
  if (Array.isArray(grid?.collectionView?.items)) return grid.collectionView.items;
  if (Array.isArray(grid?.collectionView?._view)) return grid.collectionView._view;
  if (Array.isArray(grid?.rows)) {
    return grid.rows.map((row) => row?.dataItem).filter((row) => row != null);
  }
  return [];
};

const normalizeGridSheet = (sheet, fallbackName) => {
  const grid = sheet?.grid || sheet?.flexGrid?.current?.refGrid?.current || sheet?.refGrid?.current || sheet;
  const explicitRows = Array.isArray(sheet?.rows) && !sheet.rows.some((row) => row?.dataItem) ? sheet.rows : null;
  return {
    grid,
    name: sheet?.sheetName || sheet?.name || fallbackName,
    rows: explicitRows,
    columns: Array.isArray(sheet?.columns) ? sheet.columns : null,
  };
};

export const saveGridLikeWorkbook = async (
  sheets = [],
  {
    includeColumnHeaders = true,
    includeStyles = true,
    fileName = "DataGrid.xlsx",
    isCheckedItem = false,
    newRows = [],
    includeColumns,
  } = {}
) => {
  const workbook = new ExcelJS.Workbook();
  workbook.creator = "MAXIS";
  workbook.created = new Date();

  sheets.forEach((sheet, idx) => {
    const normalized = normalizeGridSheet(sheet, `Sheet${idx + 1}`);
    const grid = normalized.grid;
    const columns = normalized.columns || getGridLikeColumns(grid, includeColumns);
    let rows = normalized.rows || getGridLikeRows(grid);

    if (newRows.length) rows = newRows;
    if (isCheckedItem) rows = rows.filter((row) => row?.ROW_CHECK === true || row?.ROW_CHECK === 1);

    const worksheet = workbook.addWorksheet(normalized.name || `Sheet${idx + 1}`);
    worksheet.columns = columns.map((column) => ({
      header: column.header,
      key: column.binding,
      width: typeof column.width === "number" ? Math.max(8, Math.min(60, Math.round(column.width / 8))) : 16,
    }));

    if (!includeColumnHeaders) worksheet.spliceRows(1, 1);

    rows.forEach((row) => {
      worksheet.addRow(
        columns.reduce((acc, column) => {
          acc[column.binding] = row?.[column.binding];
          return acc;
        }, {})
      );
    });

    if (includeStyles && includeColumnHeaders && worksheet.getRow(1)) {
      worksheet.getRow(1).font = { bold: true };
      worksheet.getRow(1).alignment = { vertical: "middle" };
    }
  });

  const buffer = await workbook.xlsx.writeBuffer();
  if (typeof document !== "undefined") {
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = fileName || "DataGrid.xlsx";
    link.click();
    URL.revokeObjectURL(link.href);
  }

  return workbook;
};

export const readExcelRows = async (file, { headerRowIndex = 0, columns = [] } = {}) => {
  const workbook = new ExcelJS.Workbook();
  const buffer = await file.arrayBuffer();
  await workbook.xlsx.load(buffer);
  const worksheet = workbook.worksheets[0];
  if (!worksheet) return [];

  const headerValues = [];
  for (let rowNumber = 1; rowNumber <= headerRowIndex + 1; rowNumber++) {
    const row = worksheet.getRow(rowNumber);
    row.eachCell({ includeEmpty: true }, (cell, colNumber) => {
      const value = getCellMergedValue(cell);
      if (value !== "") headerValues[colNumber - 1] = value;
    });
  }

  const exportColumns = columns.filter((column) => column?.binding && !EXCLUDED_EXCEL_BINDINGS.includes(column.binding));
  const rows = [];
  worksheet.eachRow({ includeEmpty: false }, (row, rowNumber) => {
    if (rowNumber <= headerRowIndex + 1) return;

    const item = {};
    let hasValue = false;
    const maxColumns = Math.max(row.cellCount, exportColumns.length || headerValues.length);
    for (let colNumber = 1; colNumber <= maxColumns; colNumber++) {
      const column = exportColumns[colNumber - 1];
      const key = column?.binding || headerValues[colNumber - 1];
      if (!key) continue;

      const value = getCellMergedValue(row.getCell(colNumber));
      if (value !== "") hasValue = true;
      item[key] = value;
    }
    if (hasValue) rows.push(item);
  });

  return rows;
};

/**
 * grid multi sheet excel download
 * @param {*} arrGrid
 * @param {*} colHeader
 * @param {*} style
 * @param {*} fileName
 * @param {*} newArrData
 * @param {*} isCheckedItem
 */
export const saveGridExcel = (
  arrGrid = [],
  colHeader = true,
  style = true,
  fileName = "",
  newArrData = [],
  isCheckedItem = false
) => {
  if (chkArray(arrGrid)) {
    return saveGridLikeWorkbook(arrGrid, {
      includeColumnHeaders: colHeader,
      includeStyles: style,
      fileName: fileName > "" ? fileName : "DataGrid.xlsx",
      newRows: newArrData,
      isCheckedItem,
      includeColumns: (column) => column.visible !== false,
    });
  } else {
    console.error("옳바른 배열 형태가 아닙니다.");
  }
};
/**
 * @typedef {object} ComponentOption
 * @property {"IMAGE"|"BUTTON"|"LINK"|"TEXT"|undefined} type - default BUTTON
 * @property {object | undefined} data - payload
 * @property {(e: MouseEvent, data: object) => void | undefined} onClick - clickEvent
 * @property {Object<string, string> | undefined} style - inline 스타일 (ex. { color: "red" })
 * @property {string | undefined} className - 클래스 이름
 * @property {string | undefined} src - Image 전용
 * @property {string | undefined} alt - Image 전용
 * @property {string | undefined} href - Link 전용
 * @property {string | undefined} target - Link 전용
 * @property {string | undefined} text - Button | Link | Text 전용
 * @property {string | undefined} buttonType
 * @property {string | undefined} colorType
 * @property {string | undefined} size
 * @property {string | undefined} iconType
 *
 */

/**
 * 셀에 컴포넌트를 세팅
 * @param {HTMLElement} cell
 * @param {ComponentOption} option
 */
export const setCellComponent = (cell, option) => {
  // Element 초기화
  cell.innerHTML = "";

  const element = createComponentElement(option);

  applyCommonAttributes(element, option);

  cell.appendChild(element);
};

/**
 * type에 따라 HTML 요소를 생성
 * @param {ComponentOption} option
 * @returns {HTMLElement}
 */
function createComponentElement(option = {}) {
  let el;
  const { type } = option;
  switch (type) {
    case "IMAGE":
      el = document.createElement("img");
      el.src = option.src ?? "";
      el.alt = option.alt ?? "";
      el.style.maxWidth = "100%";
      break;

    case "LINK":
      el = document.createElement("a");
      el.href = option.href ?? "#";
      el.textContent = option.text ?? "Link";
      el.target = option.target ?? "_blank";
      break;

    case "BUTTON":
      el = document.createElement("button");
      el.textContent = option.text ?? "Button";
      el.type = "button";
      break;
    case "TEXT":
    default:
      el = document.createElement("p");
      el.textContent = option.text ?? "input text property...";
      break;
  }

  return el;
}

/**
 * 공통 속성 및 이벤트 설정
 * @param {HTMLElement} el
 * @param {ComponentOption} option
 */
function applyCommonAttributes(el, option) {
  const { data = {}, onClick, style = {}, className, type } = option;

  el.style.cursor = "pointer";
  el.style.margin = "4px";

  // 사용자 지정 스타일 적용
  Object.assign(el.style, style);

  // className 설정
  if (className) {
    el.className = className;
  }
  if (type === "BUTTON") {
    el.className += ` ${_makeClassnames({
      buttonType: option.buttonType,
      iconType: option.iconType,
      size: option.size,
      colorType: option.colorType,
    })}`;
  }

  // 이벤트 핸들러 등록
  if (onClick) {
    el.addEventListener("click", (e) => onClick(e, data));
  }
}
const _makeClassnames = ({ buttonType, colorType, size, iconType }) => {
  const classNames = [];
  classNames.push(styles.btn);

  switch (buttonType) {
    case "default":
      break;
    case "close":
      classNames.push(styles.closeBtn);
      break;
    case "search":
      classNames.push(styles.searchBtn);
      break;
    case "init":
      classNames.push(styles.initBtn);
      break;
  }

  if (buttonType === "default") {
    switch (colorType) {
      case "default":
        classNames.push(styles.colorDefault);
        break;
      case "default-outline":
        classNames.push(styles.colorDefaultOutline);
        break;
      case "main":
        classNames.push(styles.colorMain);
        break;
      case "main-outline":
        classNames.push(styles.colorRedMain);
        break;
      case "sub1":
        classNames.push(styles.colorSub1);
        break;
      case "sub1-outline":
        classNames.push(styles.colorSub1Outline);
        break;
      case "sub2":
        classNames.push(styles.colorSub2);
        break;
      case "sub2-outline":
        classNames.push(styles.colorSub2Outline);
        break;
      case "sub3":
        classNames.push(styles.colorSub3);
        break;
      case "sub3-outline":
        classNames.push(styles.colorSub3Outline);
        break;
      case "sub4":
        classNames.push(styles.colorSub4);
        break;
      case "sub5":
        classNames.push(styles.colorSub5);
        break;
      case "sub6":
        classNames.push(styles.colorSub6Line);
        break;
      case "sub7Green":
        classNames.push(styles.sub7GreenLine);
        break;
      case "pageOn":
        classNames.push(styles.pageOn);
        break;
      case "pageDefault":
        classNames.push(styles.pageDefault);
        break;
      case "prevList":
        classNames.push(styles.prevList);
        break;
      case "colorNoBorder":
        classNames.push(styles.colorNoBorder);
        break;
      case "roundRed":
        classNames.push(styles.roundRed);
        break;
    }
  }

  switch (size) {
    case "default":
      break;
    case "small":
      classNames.push(styles.small);
      break;
    case "middle":
      classNames.push(styles.middle);
      break;
    case "large":
      classNames.push(styles.large);
      break;
  }

  if (iconType) {
    classNames.push(styles.icon);
    classNames.push(styles[iconType]);
  }

  return classNames.join(" ");
};
